// =========================================================================
// The account keys this browser saw registered, kept here so that every
// address the dashboard shows can be derived AGAIN on this machine and
// held against the server's word (audit of 21 Oct 2026, E2).
//
// Until 21 Oct a deposit address — shown large, put into a QR code for the
// customer, labelled "path 0/i under the account key" — was whatever the
// server answered. The browser had held the account's public key and chain
// code at registration (Keys.tsx) and kept nothing. Now it keeps the PUBLIC
// parts (no key ever leaves a Ledger), and derives: an address the key does
// not derive at the path the server names is said in red and never handed
// out. A browser that saw no registration says "unchecked here" — never a
// guess either way.
//
// Public material only: an xpub derives addresses, never spends.
// =========================================================================

import {
  xpubFromAccountKey, tronAddressFromXpub, HARDENED_OFFSET, hexToBytes,
} from '../../../signer/src/hd.mjs';
import type { Network } from '../../../cosign/src/verify';

export interface LocalAccountKey {
  account: number;
  xpub: string;
  firstAddress: string;
  keptAt: string;
}

const storageKey = (network: Network) => `dubaiotc.custody.account-keys.v1.${network}`;

function readAll(network: Network): Record<string, LocalAccountKey> {
  try {
    const raw = localStorage.getItem(storageKey(network));
    const parsed = raw ? JSON.parse(raw) as unknown : null;
    return parsed && typeof parsed === 'object' ? parsed as Record<string, LocalAccountKey> : {};
  } catch {
    return {};
  }
}

/** Keep an account key this browser just saw registered. False when the browser will not store. */
export function rememberAccountKey(
  network: Network,
  key: { account: number; publicKey: string; chainCode: string; parentFingerprint?: string | null; firstAddress: string },
  now: Date = new Date(),
): boolean {
  try {
    const fingerprint = key.parentFingerprint && /^[0-9a-f]{8}$/i.test(key.parentFingerprint) ? parseInt(key.parentFingerprint, 16) : 0;
    const xpub = xpubFromAccountKey({
      publicKey: hexToBytes(key.publicKey), chainCode: hexToBytes(key.chainCode),
      childNumber: key.account + HARDENED_OFFSET, parentFingerprint: fingerprint,
    });
    // The first address the device showed must be what the parts derive, or the parts are not that key.
    if (tronAddressFromXpub(xpub, 0) !== key.firstAddress) return false;
    const all = readAll(network);
    all[String(key.account)] = { account: key.account, xpub, firstAddress: key.firstAddress, keptAt: now.toISOString() };
    localStorage.setItem(storageKey(network), JSON.stringify(all));
    return true;
  } catch {
    return false;
  }
}

/** The key this browser holds for an account, or null. */
export function localAccountKey(network: Network, account: number): LocalAccountKey | null {
  const k = readAll(network)[String(account)];
  return k && typeof k.xpub === 'string' ? k : null;
}

export type AddressCheck = 'derived' | 'mismatch' | 'unchecked';

/**
 * Hold an address the server shows against what this browser derives at the
 * path the server names under the account's key.
 *
 *   derived    this browser derives exactly this address there
 *   mismatch   it derives ANOTHER address there — the server's word is wrong;
 *              never give the address out. Also: a key is kept here and the
 *              server names NO path, or one this key cannot derive at — a
 *              server that can drop the path could otherwise name any
 *              address and pass (Astra, 22 Oct 2026)
 *   unchecked  no key kept here for that account
 */
export function checkAddress(network: Network, a: { address: string; derivation: string | null | undefined; account?: number }): AddressCheck {
  const account = Number.isInteger(a.account) ? (a.account as number) : 0;
  const key = localAccountKey(network, account);
  if (!key) return 'unchecked';
  const m = /^(\d+)\/(\d+)$/.exec(String(a.derivation ?? '').trim());
  if (!m) return 'mismatch';
  try {
    return tronAddressFromXpub(key.xpub, Number(m[2]), { change: Number(m[1]) }) === a.address ? 'derived' : 'mismatch';
  } catch {
    return 'mismatch';
  }
}

/** The words the screen uses — short, as the operator asked (21 Oct 2026). */
export const CHECK_WORDS: Readonly<Record<AddressCheck, string>> = Object.freeze({
  derived: 'Checked — this browser derives it from the registered key',
  mismatch: 'Mismatch — not what the registered key derives, or no path to derive at. Do not give this address out; report it',
  unchecked: 'Unchecked here — the key was registered in another browser',
});
